import { Box, Button, Divider, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useCallback, useMemo } from 'react'
import TipTapEditor from './TipTapEditor'
import IssuedDietPlansPanel from './dietPlan/IssuedDietPlansPanel'
import {
  NUTRITION_CONSULTATION_SECTIONS,
  NUTRITION_DIET_PLAN_SECTION_ID,
} from '../nutritionConsultationSectionConfig'

const MEAL_SLOT_OPTIONS = [
  { code: 'breakfast', label: 'Breakfast' },
  { code: 'mid_morning', label: 'Mid-morning' },
  { code: 'lunch', label: 'Lunch' },
  { code: 'evening_snack', label: 'Evening snack' },
  { code: 'dinner', label: 'Dinner' },
  { code: 'post_dinner', label: 'Post-dinner' },
]

/**
 * Renders the nutrition discipline EMR sections inside TherapyReportMakingPage.
 *
 * - When `emrSections` (from the booked event's per-report-type emrSections payload) is non-empty,
 *   it drives section ordering, titles, and visibility (selected + enabled, !printOnly).
 * - Otherwise falls back to NUTRITION_CONSULTATION_SECTIONS so the editor still works pre-seed.
 * - Diet plan section also exposes a structured meal-by-meal table writing into
 *   nutritionSection.dietPlan.
 *
 * Reads/writes happen via `nutritionSection` on the therapyReport draft. The parent owns the draft;
 * this component is a controlled renderer.
 */
export default function NutritionConsultationSections({
  therapyReportData,
  setTherapyReportData,
  emrSections,
  onUserEdit,
  reportKey,
  bookedEventId,
  patientName,
  defaultLanguage = 'en',
}) {
  const nutritionSection = therapyReportData?.nutritionSection ?? {}

  const sectionsToRender = useMemo(() => {
    if (Array.isArray(emrSections) && emrSections.length > 0) {
      const orderedFromBackend = emrSections
        .filter((s) => s?.enabled !== false && s?.printOnly !== true)
        .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
      // Map backend ids to known config rows so we know which field to bind to
      const selectedSections = orderedFromBackend
        .filter((s) => Boolean(s?.selected))
        .map((bs) => {
          const cfg = NUTRITION_CONSULTATION_SECTIONS.find((c) => c.id === bs.id)
          if (!cfg) return null
          return { ...cfg, title: bs.title ?? cfg.title }
        })
        .filter(Boolean)
      if (selectedSections.length > 0) {
        return selectedSections
      }
      const fallbackBackendSections = orderedFromBackend
        .map((bs) => {
          const cfg = NUTRITION_CONSULTATION_SECTIONS.find((c) => c.id === bs.id)
          if (!cfg) return null
          return { ...cfg, title: bs.title ?? cfg.title }
        })
        .filter(Boolean)
      if (fallbackBackendSections.length > 0) {
        return fallbackBackendSections
      }
    }
    return NUTRITION_CONSULTATION_SECTIONS
  }, [emrSections])

  const updateNutritionField = useCallback(
    (field, value) => {
      onUserEdit?.()
      setTherapyReportData((prev) => ({
        ...(prev ?? {}),
        nutritionSection: {
          ...(prev?.nutritionSection ?? {}),
          [field]: value,
        },
      }))
    },
    [onUserEdit, setTherapyReportData],
  )

  const updateDietPlanEntry = useCallback(
    (index, patch) => {
      onUserEdit?.()
      setTherapyReportData((prev) => {
        const entries = Array.isArray(prev?.nutritionSection?.dietPlan)
          ? [...prev.nutritionSection.dietPlan]
          : []
        entries[index] = { ...(entries[index] ?? {}), ...patch }
        return {
          ...(prev ?? {}),
          nutritionSection: {
            ...(prev?.nutritionSection ?? {}),
            dietPlan: entries,
          },
        }
      })
    },
    [onUserEdit, setTherapyReportData],
  )

  const addDietPlanEntry = useCallback(() => {
    onUserEdit?.()
    setTherapyReportData((prev) => {
      const entries = Array.isArray(prev?.nutritionSection?.dietPlan)
        ? [...prev.nutritionSection.dietPlan]
        : []
      // Cap to 8 to match the backend section def
      if (entries.length >= 8) return prev
      entries.push({ mealSlot: '', time: '', items: '', approxCalories: '', macros: '', notes: '' })
      return {
        ...(prev ?? {}),
        nutritionSection: {
          ...(prev?.nutritionSection ?? {}),
          dietPlan: entries,
        },
      }
    })
  }, [onUserEdit, setTherapyReportData])

  const removeDietPlanEntry = useCallback(
    (index) => {
      onUserEdit?.()
      setTherapyReportData((prev) => {
        const entries = Array.isArray(prev?.nutritionSection?.dietPlan)
          ? [...prev.nutritionSection.dietPlan]
          : []
        entries.splice(index, 1)
        return {
          ...(prev ?? {}),
          nutritionSection: {
            ...(prev?.nutritionSection ?? {}),
            dietPlan: entries,
          },
        }
      })
    },
    [onUserEdit, setTherapyReportData],
  )

  const dietPlanEntries = Array.isArray(nutritionSection?.dietPlan) ? nutritionSection.dietPlan : []

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {sectionsToRender.map((section) => (
        <Box
          key={section.id}
          sx={{
            marginBottom: '20px',
            marginTop: '24px',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <Box
            sx={{
              padding: '12px 16px',
              background: 'rgba(99, 102, 241, 0.04)',
              borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
              fontWeight: 600,
              fontSize: '15px',
              color: '#1e293b',
            }}
          >
            {section.title}
          </Box>
          <Box sx={{ padding: '16px' }}>
            <TipTapEditor
              key={`${section.id}-${reportKey ?? 'draft'}`}
              contentMinHeight={72}
              value={nutritionSection?.[section.field] ?? ''}
              onChange={(html) => updateNutritionField(section.field, html ?? '')}
              placeholder={`Write ${section.title.toLowerCase()}...`}
            />

            {section.id === NUTRITION_DIET_PLAN_SECTION_ID && (
              <Box sx={{ marginTop: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    Meal-by-meal plan
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addDietPlanEntry}
                    disabled={dietPlanEntries.length >= 8}
                    sx={{ textTransform: 'none' }}
                  >
                    Add meal
                  </Button>
                </Box>

                {dietPlanEntries.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Add a row per meal slot to capture timing, items, calories and macros.
                  </Typography>
                )}

                {dietPlanEntries.map((entry, index) => (
                  <Box
                    key={`diet-plan-${index}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 2.4fr 1fr 1.4fr 0.4fr' },
                      gap: 1,
                      alignItems: 'center',
                      marginBottom: 1,
                      padding: 1,
                      borderRadius: '8px',
                      background: 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label="Meal"
                      value={entry?.mealSlot ?? ''}
                      onChange={(e) => updateDietPlanEntry(index, { mealSlot: e.target.value })}
                    >
                      <MenuItem value="">-</MenuItem>
                      {MEAL_SLOT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.code} value={opt.code}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="time"
                      label="Time"
                      InputLabelProps={{ shrink: true }}
                      value={entry?.time ?? ''}
                      onChange={(e) => updateDietPlanEntry(index, { time: e.target.value })}
                    />
                    <TextField
                      size="small"
                      label="Items / Portions"
                      multiline
                      maxRows={3}
                      value={entry?.items ?? ''}
                      onChange={(e) => updateDietPlanEntry(index, { items: e.target.value })}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="kcal"
                      value={entry?.approxCalories ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value
                        updateDietPlanEntry(index, {
                          approxCalories: raw === '' ? '' : Number(raw),
                        })
                      }}
                    />
                    <TextField
                      size="small"
                      label="Macros"
                      placeholder="e.g. C 50g / P 20g / F 10g"
                      value={entry?.macros ?? ''}
                      onChange={(e) => updateDietPlanEntry(index, { macros: e.target.value })}
                    />
                    <IconButton
                      onClick={() => removeDietPlanEntry(index)}
                      aria-label="Remove meal"
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                {/* Template-driven issued diet plans live alongside the lightweight inline table.
                    The inline table captures quick meal-by-meal notes; the panel below issues a
                    full, localised, customisable diet plan as its own report. */}
                {bookedEventId && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <IssuedDietPlansPanel
                      bookedEventId={bookedEventId}
                      patientName={patientName}
                      defaultLanguage={defaultLanguage}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
